import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Users, Upload, X } from "lucide-react";

export default function TeamCreate() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    sport: "football",
    description: "",
    logo: "",
  });

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleUploadLogo = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      const r = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("logo", r.data.url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearLogo = () => update("logo", "");

  const sports = [
    "football",
    "basketball",
    "tennis",
    "padel",
    "volleyball",
    "squash",
    "badminton",
  ];

  const canSubmit = () => form.name.trim() && form.sport;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const payload = { name: form.name.trim(), sport: form.sport };
      if (form.description.trim()) payload.description = form.description.trim();
      if (form.logo.trim()) payload.logo = form.logo.trim();
      const r = await api.post("/teams", payload);
      toast.success(t("team.created"));
      navigate(`/teams/${r.data.team.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[600px] mx-auto px-5 md:px-10 py-10">
        <button
          onClick={() => navigate("/teams")}
          className="btn-outline px-4 py-2 text-xs tracking-widest uppercase font-semibold flex items-center gap-2 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> {t("common.back")}
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-full bg-volt/10 flex items-center justify-center">
            <Users className="h-5 w-5 text-volt" />
          </div>
          <div>
            <p className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground">
              {t("team.create_eyebrow")}
            </p>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
              {t("team.create_title")}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-border p-6 md:p-8 space-y-6">
          {/* Team Name */}
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">
              {t("team.team_name")} <span className="text-red-400">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder={t("team.team_name_placeholder")}
              className="w-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-volt"
            />
          </div>

          {/* Sport */}
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">
              {t("team.sport")} <span className="text-red-400">*</span>
            </label>
            <select
              value={form.sport}
              onChange={(e) => update("sport", e.target.value)}
              className="w-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-volt"
            >
              {sports.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">
              {t("team.description")} <span className="text-muted-foreground/50">({t("common.optional")})</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder={t("team.description_placeholder")}
              rows={4}
              className="w-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-volt resize-none"
            />
          </div>

          {/* Logo */}
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">
              {t("team.logo_url")} <span className="text-muted-foreground/50">({t("common.optional")})</span>
            </label>
            <div className="flex items-start gap-4">
              {form.logo ? (
                <div className="relative w-24 h-24 rounded-full overflow-hidden border border-border flex-shrink-0 group">
                  <img src={form.logo} alt="Team logo" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full border-2 border-dashed border-border bg-muted flex items-center justify-center flex-shrink-0">
                  <Users className="h-8 w-8 text-muted-foreground/50" />
                </div>
              )}
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full border-2 border-dashed border-border bg-muted/30 hover:bg-muted/50 transition-colors rounded-lg px-4 py-6 flex flex-col items-center justify-center gap-2 disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin h-6 w-6 text-muted-foreground" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      <span className="text-xs text-muted-foreground">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Click to upload logo</span>
                      <span className="text-[10px] text-muted-foreground/50">PNG, JPG, WEBP (max 5MB)</span>
                    </>
                  )}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleUploadLogo}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !canSubmit()}
            className="btn-volt px-8 py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                {t("common.loading")}
              </>
            ) : (
              <>
                <Users className="h-4 w-4" />
                {t("team.create")}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
